import React, { useState, useEffect } from 'react';
import { OuterWrapper, EventListWrapper, PortCallEventItemWrapper, BerthCallEventWrapper, NoEventsWrapper } from './FilterEvents.styles';
import useRotation from '../../../app/useRotation';
import { useRotationStore } from '../../../context/RotationContext';
import { PortCall } from '../../../types/ViewModel/PortCall';
import { useEventStore } from '../../../context/EventContext';
import useEvent from '../../../app/useEvent';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ReportLocationEnum } from '../../../enums/reportLocationEnum';
import { BerthCall } from '../../../types/ViewModel/BerthCall';
import { CargoOperation } from '../../../types/ViewModel/CargoOperation';
import { TankersEvent } from '../../../types/ViewModel/TankersEvent';
import eventUtils from '../../../utils/event/eventUtils';
import { useEventTypeStore } from '../../../context/EventTypeContext';
import { EventTypeData } from '../../../types/ViewModel/EventTypeData';
import { RotationTreeEnum } from '../../../enums/rotationTreeEnum';
import { IRotationNode } from '../../../types/ViewModel/IRotationNode';
import { EventTypeFilter } from '../../../types/ViewModel/EventTypeFilter';

type Props = {
    eventTypeFilter: EventTypeFilter;
};

const FilterEventsComponent: React.FC<Props> = (props) => {
    const [selectedPortCall, setSelectedPortCall] = useState<PortCall | null>(null);
    const [filteredEventList, setFilteredEventList] = useState<TankersEvent[]>([]);
    const { updatedRotation, selectedNode } = useRotationStore();
    const { getPortCallFromNode } = useRotation();
    const { fullEventArray } = useEventStore();
    const { filterOnContext, filterOnContextSingleLevel } = useEvent();
    const { getActivity, getUtcOffset, getEventDate } = eventUtils();
    const { eventTypeDataPort, eventTypeDataSea } = useEventTypeStore();

    useEffect(() => {
        if (selectedNode) {
            setSelectedPortCall(getPortCallFromNode(selectedNode));
            updateFilteredEventList(selectedNode);
        }
    }, [selectedNode]);

    const filterCriteriaOk = (ev: TankersEvent): boolean => {
        const evTypeGroup = props.eventTypeFilter.eventTypeIdList.find((id) => id === ev.eventTypeId);
        if (evTypeGroup) return true;
        return false;
    };

    const updateFilteredEventList = (selNode: IRotationNode) => {
        switch (selNode.type) {
            case RotationTreeEnum.Sea:
                setFilteredEventList(
                    fullEventArray.filter(
                        (ev) =>
                            !ev.deleted && ev.eventTypeContextId === ReportLocationEnum.Sea && ev.portCallId === selNode.id && filterCriteriaOk(ev)
                    )
                );
                break;
            case RotationTreeEnum.PortCall:
                setFilteredEventList(
                    fullEventArray.filter(
                        (ev) =>
                            !ev.deleted && ev.eventTypeContextId === ReportLocationEnum.Port && ev.portCallId === selNode.id && filterCriteriaOk(ev)
                    )
                );
                break;
            case RotationTreeEnum.BerthCall:
                setFilteredEventList(
                    fullEventArray.filter(
                        (ev) =>
                            !ev.deleted &&
                            ev.eventTypeContextId === ReportLocationEnum.Port &&
                            ev.berthCallId &&
                            ev.berthCallId === selNode.id &&
                            filterCriteriaOk(ev)
                    )
                );
                break;
            case RotationTreeEnum.CargoOperation:
                setFilteredEventList(
                    fullEventArray.filter(
                        (ev) =>
                            !ev.deleted &&
                            ev.eventTypeContextId === ReportLocationEnum.Port &&
                            ev.cargoOperationId &&
                            ev.cargoOperationId === selNode.id &&
                            filterCriteriaOk(ev)
                    )
                );
                break;
        }
    };

    const portCallName = () => {
        if (selectedPortCall)
            return (
                <span className="rotation-port-name">
                    <span>{selectedPortCall.voyageNumberPresentationString}</span>
                    <span className="sep"> - </span>
                    {selectedPortCall.name}
                    {getPortClusterIcon(selectedPortCall)}
                </span>
            );
        return null;
    };

    const showBerthName = (bc: BerthCall): boolean => {
        const evList = filteredEventList.filter((ev) => ev.berthCallId && ev.berthCallId === bc.berthCallId);
        if (selectedNode && selectedNode.type === RotationTreeEnum.PortCall) return evList.length > 0;
        if (selectedNode && selectedNode.type === RotationTreeEnum.BerthCall) {
            return selectedNode.id === bc.berthCallId;
        }
        if (selectedNode && selectedNode.type === RotationTreeEnum.CargoOperation) {
            const cargoOp = bc.cargoOperationArray.find((co) => co.cargoOperationId === selectedNode.id);
            if (cargoOp) return true;
        }
        return false;
    };

    const berthCallName = (berthCallItem: BerthCall) => {
        return <span className="rotation-berth-name">{berthCallItem.name}</span>;
    };

    const listBerthCalls = (bcList: BerthCall[]) => {
        if (bcList && bcList.length > 0) {
            const bcListFiltered = bcList.filter((bc) => showBerthName(bc));
            if (bcListFiltered && bcListFiltered.length > 0) {
                return (
                    <BerthCallEventWrapper>
                        {bcListFiltered.map((bc: BerthCall, i) => {
                            const cargoOpListFiltered = bc.cargoOperationArray.filter((co) => showCargoOperationName(co));
                            return (
                                <div key={`cargoOpListFiltered_${i}`}>
                                    {berthCallName(bc)}
                                    {listEvents(bc, filterOnContext(bc, filteredEventList))}
                                    {cargoOpListFiltered.map((co, j) => {
                                        return (
                                            <div key={`listEvents_cargoOpListFiltered_${j}`}>
                                                {cargoOperationName(co)}
                                                {listEvents(co, filterOnContext(co, filteredEventList))}
                                            </div>
                                        );
                                    })}
                                </div>
                            );
                        })}
                    </BerthCallEventWrapper>
                );
            }
        }
        return null;
    };

    const showCargoOperationName = (co: CargoOperation): boolean => {
        const evList = filteredEventList.filter((ev) => ev.cargoOperationId && ev.cargoOperationId === co.cargoOperationId);
        if (selectedNode && selectedNode.type === RotationTreeEnum.PortCall) return evList.length > 0;
        if (selectedNode && selectedNode.type === RotationTreeEnum.BerthCall) return evList.length > 0;
        if (selectedNode && selectedNode.type === RotationTreeEnum.CargoOperation) {
            return selectedNode.id === co.cargoOperationId;
        }
        return false;
    };

    const cargoOperationName = (co: CargoOperation) => {
        return <span className="rotation-cargo-name">{co.name}</span>;
    };

    const getPortClusterIcon = (portCallItem: PortCall) => {
        if (portCallItem.isInPortCluster && portCallItem.reportLocation === ReportLocationEnum.Port) {
            return (
                <span className="is-in-port-cluster">
                    <FontAwesomeIcon title="Port cluster" icon="project-diagram" />
                </span>
            );
        }
        return null;
    };

    const getEventTypeData = (event: TankersEvent): EventTypeData => {
        return event.eventTypeContextId === ReportLocationEnum.Port ? eventTypeDataPort : eventTypeDataSea;
    };

    const listEvents = (selNode: IRotationNode, filterNodeEvList: TankersEvent[]) => {
        if (selNode) {
            const evList = filterOnContextSingleLevel(selNode, filteredEventList);
            if (evList && evList.length > 0) {
                return (
                    <EventListWrapper>
                        {evList.map((ev: TankersEvent, i) => {
                            return (
                                <PortCallEventItemWrapper key={`evList_${i}`}>
                                    <div title={getUtcOffset(ev.eventDate)} className="event-date">
                                        {getEventDate(ev.eventDate.actualDateTimeLocal)}
                                    </div>
                                    <div
                                        className="event-display-name"
                                        dangerouslySetInnerHTML={{
                                            __html: getActivity(updatedRotation, filteredEventList, selNode, getEventTypeData(ev), ev, true),
                                        }}
                                    />
                                </PortCallEventItemWrapper>
                            );
                        })}
                    </EventListWrapper>
                );
            } else if (selNode.id === selectedNode?.id && filterNodeEvList.length === 0) {
                return <NoEventsWrapper>No events to display</NoEventsWrapper>;
            }
        }
        return null;
    };

    return (
        <>
            {selectedNode && selectedPortCall && (
                <OuterWrapper>
                    {portCallName()}
                    {listEvents(selectedPortCall, filterOnContext(selectedPortCall, filteredEventList))}
                    {listBerthCalls(selectedPortCall.berthCallArray)}
                </OuterWrapper>
            )}
        </>
    );
};

export default FilterEventsComponent;
